import { useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  Star,
  Save,
  CheckCircle2,
  AlertTriangle,
  LayoutTemplate as LayoutTemplateIcon,
  Infinity as InfinityIcon,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import { LayoutBuilder, DEFAULT_SCALE, MIN_ZOOM, MAX_ZOOM } from "../template/LayoutBuilder";
import { DEFAULT_TEMPLATE } from "../template/templateTypes";
import type { LayoutTemplate as LayoutTemplateData } from "../template/templateTypes";
import api from "../../lib/api";

/** Een opgeslagen template-rij uit de backend (GET /api/layout-templates). */
interface SavedTemplate {
  id: string;
  name: string;
  isDefault: boolean;
  data: LayoutTemplateData;
  updatedAt: string;
  _count?: { invoices: number; quotes: number };
}

interface LayoutManagerProps {
  companyId: string;
  company: any;
  isSuperadmin: boolean;
}

/** Sentinel-waarde voor "nieuwe template aan het opstellen" (nog geen id). */
const NEW_SENTINEL = "__new__";

function cloneTemplate(t: LayoutTemplateData): LayoutTemplateData {
  return JSON.parse(JSON.stringify(t));
}

export function LayoutManager({ companyId, company, isSuperadmin }: LayoutManagerProps) {
  const [templates, setTemplates] = useState<SavedTemplate[]>([]);
  /** Platform-template (isGlobal) — read-only voor bedrijven die hem niet bezitten. */
  const [globalTemplate, setGlobalTemplate] = useState<SavedTemplate | null>(null);
  const [used, setUsed] = useState(0);
  const [limit, setLimit] = useState<number | null>(null);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftData, setDraftData] = useState<LayoutTemplateData | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<SavedTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [zoom, setZoom] = useState(DEFAULT_SCALE);

  const load = async () => {
    try {
      const { data } = await api.get("/api/layout-templates");
      const list: SavedTemplate[] = (data.templates || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        isDefault: t.isDefault,
        data: t.data as LayoutTemplateData,
        updatedAt: t.updatedAt,
        _count: t._count,
      }));
      setTemplates(list);
      setUsed(data.used ?? 0);
      setLimit(data.limit ?? null);
      setGlobalTemplate(
        data.globalTemplate
          ? {
              id: data.globalTemplate.id,
              name: data.globalTemplate.name,
              isDefault: false,
              data: data.globalTemplate.data as LayoutTemplateData,
              updatedAt: data.globalTemplate.updatedAt,
            }
          : null
      );

      // Houd een geldige selectie: bewaar actieve id indien nog aanwezig,
      // anders pak de default (of de eerste) — nooit zomaar alles deselecteren.
      setActiveId((prev) => {
        if (prev === NEW_SENTINEL) return prev;
        if (prev && list.some((t) => t.id === prev)) return prev;
        const def = list.find((t) => t.isDefault);
        return def?.id || list[0]?.id || null;
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || "Templates laden mislukt");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Wissel van selectie → seed naam/data uit de gekozen template (of default).
  useEffect(() => {
    if (activeId === NEW_SENTINEL) {
      setDraftName("Nieuwe template");
      setDraftData(cloneTemplate(DEFAULT_TEMPLATE));
      setError("");
      setSuccess("");
      return;
    }
    const t = templates.find((x) => x.id === activeId);
    if (t) {
      setDraftName(t.name);
      setDraftData(t.data ? cloneTemplate(t.data) : cloneTemplate(DEFAULT_TEMPLATE));
      setError("");
      setSuccess("");
    } else if (globalTemplate && activeId === globalTemplate.id) {
      // Platform-template (van een ander bedrijf) — read-only tonen.
      setDraftName(globalTemplate.name);
      setDraftData(globalTemplate.data ? cloneTemplate(globalTemplate.data) : cloneTemplate(DEFAULT_TEMPLATE));
      setError("");
      setSuccess("");
    } else {
      setDraftData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, templates, globalTemplate]);

  const atLimit = !isSuperadmin && limit != null && used >= limit;

  const startNew = () => {
    if (atLimit) return;
    setActiveId(NEW_SENTINEL);
  };

  const flash = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3500);
  };

  const save = async () => {
    setError("");
    if (!draftName.trim()) return setError("Geef de template een naam.");
    if (!draftData) return setError("Ongeldige template-data.");
    setSaving(true);
    try {
      if (activeId === NEW_SENTINEL) {
        const { data: created } = await api.post("/api/layout-templates", {
          name: draftName.trim(),
          data: draftData,
          isDefault: false,
        });
        await load();
        setActiveId(created.id);
        flash("Template aangemaakt.");
      } else {
        await api.patch(`/api/layout-templates/${activeId}`, {
          name: draftName.trim(),
          data: draftData,
        });
        await load();
        flash("Template opgeslagen.");
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const makeDefault = async (t: SavedTemplate) => {
    setError("");
    try {
      await api.post(`/api/layout-templates/${t.id}/default`);
      await load();
      flash(`"${t.name}" is nu de standaardlay-out.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Standaard instellen mislukt");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError("");
    try {
      await api.delete(`/api/layout-templates/${deleteTarget.id}`);
      setDeleteTarget(null);
      await load();
      flash("Template verwijderd.");
    } catch (err: any) {
      setError(err?.response?.data?.error || "Verwijderen mislukt");
    } finally {
      setDeleting(false);
    }
  };

  const openRemaining = limit == null ? null : Math.max(0, limit - used);

  /** Geselecteerde bestaande template (null bij "nieuwe template" of niets). */
  const selectedTemplate = templates.find((t) => t.id === activeId) || null;
  /** De platform-template is hier geselecteerd en hoort niet bij dit bedrijf
   *  (alleen lezen; de eigenaar bewerkt hem in zijn eigen instellingen). */
  const viewingForeignGlobal =
    !!globalTemplate && activeId === globalTemplate.id && !selectedTemplate;

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="p-3 rounded-xl bg-brand-50/80 text-brand-600">
          <LayoutTemplateIcon size={26} />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-slate-800">Lay-out voor offertes &amp; facturen</h2>
          <p className="text-sm text-slate-500">
            Maak meerdere lay-outtemplates en markeer er één als standaard. Zonder standaard kies je
            per factuur/offerte welke template wordt gebruikt.
          </p>
        </div>
        <div className="text-right shrink-0">
          <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 bg-slate-100 rounded-full px-3 py-1.5">
            {limit == null ? (
              <>
                {used} <InfinityIcon size={16} /> templates
              </>
            ) : (
              <>
                {used} / {limit} templates
              </>
            )}
          </span>
          {openRemaining != null && (
            <p className="text-[0.6875rem] text-slate-400 mt-1">{openRemaining} nog open</p>
          )}
        </div>
      </div>

      {/* Bovenbalk: compact, lichtblauw glossy — Naam · Opslaan · Template · Nieuwe · Zoom */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-sky-100 bg-gradient-to-r from-sky-50 via-blue-50/70 to-sky-50/60 backdrop-blur-sm px-2.5 py-1.5 shadow-liquid">
        {/* Naam */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Naam</span>
          <input
            placeholder="Template-naam"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            disabled={!activeId}
            className="w-40 px-3 py-1.5 text-sm rounded-lg border border-sky-100 bg-white/80 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-300/40 focus:border-sky-300 focus:bg-white transition-all disabled:bg-slate-50 disabled:text-slate-400"
          />
        </div>

        {/* Opslaan */}
        <Button
          size="sm"
          onClick={save}
          loading={saving}
          disabled={!activeId || viewingForeignGlobal}
          title={viewingForeignGlobal ? "De platform-template kun je hier alleen bekijken" : !activeId ? "Selecteer eerst een template" : undefined}
        >
          <Save size={15} /> Opslaan
        </Button>

        {/* Template-keuze (★ = standaard) */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Template</span>
          <select
            value={activeId ?? ""}
            onChange={(e) => setActiveId(e.target.value || null)}
            disabled={templates.length === 0 && activeId !== NEW_SENTINEL}
            className="min-w-[12rem] rounded-lg border border-sky-100 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:border-sky-300 focus:ring-2 focus:ring-sky-300/40 outline-none disabled:bg-slate-50 disabled:text-slate-400"
          >
            {templates.length === 0 && activeId !== NEW_SENTINEL && (
              <option value="">Nog geen templates</option>
            )}
            {activeId === NEW_SENTINEL && (
              <option value={NEW_SENTINEL}>Nieuwe template (nog opslaan)</option>
            )}
            {templates
              .filter((t) => t.id !== globalTemplate?.id)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.isDefault ? " ★" : ""}
                  {" · "}
                  {(t._count?.invoices || 0) + (t._count?.quotes || 0)} doc
                </option>
              ))}
            {globalTemplate && !templates.some((t) => t.id === globalTemplate.id) && (
              <option key={globalTemplate.id} value={globalTemplate.id}>
                {globalTemplate.name} (platform)
              </option>
            )}
          </select>
          {selectedTemplate && !viewingForeignGlobal && (
            <button
              type="button"
              onClick={() => !selectedTemplate.isDefault && makeDefault(selectedTemplate)}
              disabled={saving || selectedTemplate.isDefault}
              title={selectedTemplate.isDefault ? "Dit is de standaardtemplate" : "Als standaard instellen"}
              className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border ${
                selectedTemplate.isDefault
                  ? "border-amber-300 bg-amber-100 text-amber-600"
                  : "border-sky-100 bg-white text-slate-400 hover:bg-amber-50 hover:text-amber-600"
              }`}
            >
              <Star size={15} className={selectedTemplate.isDefault ? "fill-amber-400" : ""} />
            </button>
          )}
          {selectedTemplate && !viewingForeignGlobal && (
            <button
              type="button"
              title="Verwijderen"
              onClick={() => setDeleteTarget(selectedTemplate)}
              className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-sky-100 bg-white text-slate-400 hover:bg-rose-50 hover:text-rose-600"
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>

        {/* Nieuwe template — alleen voor wie recht heeft (binnen pakket-quota) */}
        <Button
          size="sm"
          variant={atLimit ? "outline" : "primary"}
          disabled={atLimit}
          onClick={startNew}
          title={atLimit ? `Limiet bereikt (${used}/${limit})` : "Nieuwe template aanmaken"}
        >
          <Plus size={15} /> Nieuwe template
        </Button>
        {atLimit && (
          <p className="text-[0.6875rem] text-amber-600 flex items-center gap-1">
            <AlertTriangle size={13} /> Limiet bereikt ({used}/{limit})
          </p>
        )}

        {/* Zoom — in/uit-zoemen van het A4-template */}
        <div className="ml-auto flex items-center gap-1 pl-2.5 border-l border-sky-100">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Zoom</span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - 0.1).toFixed(2)))}
            className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-sky-100 bg-white text-slate-600 hover:bg-sky-50 hover:text-sky-700"
            title="Uitzoomen"
          >
            <ZoomOut size={15} />
          </button>
          <span className="text-xs tabular-nums w-10 text-center text-slate-700">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + 0.1).toFixed(2)))}
            className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-sky-100 bg-white text-slate-600 hover:bg-sky-50 hover:text-sky-700"
            title="Inzoomen"
          >
            <ZoomIn size={15} />
          </button>
          <button
            type="button"
            onClick={() => setZoom(DEFAULT_SCALE)}
            className="text-[11px] text-sky-600 hover:text-sky-700 font-medium"
            title="Terug naar standaard"
          >
            reset
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
          <AlertTriangle size={18} /> {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
          <CheckCircle2 size={18} /> {success}
        </div>
      )}

      {/* Editor — volledige breedte (template-keuze zit in de bovenbalk) */}
      {activeId && draftData ? (
        <LayoutBuilder
          value={draftData}
          onChange={(v) => setDraftData(v)}
          company={company}
          zoom={zoom}
          onZoomChange={setZoom}
        />
      ) : (
        <div className="flex items-center justify-center text-sm text-slate-400 py-20">
          Maak een nieuwe template aan of kies er een via de keuzelijst hierboven.
        </div>
      )}

      {/* Verwijder-bevestiging */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Template verwijderen"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Annuleren
            </Button>
            <Button variant="danger" onClick={confirmDelete} loading={deleting}>
              Verwijderen
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Weet je zeker dat je template <strong>{deleteTarget?.name}</strong> wilt verwijderen?
            Documenten die deze template gebruikten vallen terug op de standaardlay-out.
          </p>
          {deleteTarget?.isDefault && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              <AlertTriangle size={18} className="shrink-0 mt-0.5" />
              <span>
                Dit is de standaardtemplate. Na verwijderen is er geen standaard meer — kies een
                andere of laat documenten per stuk kiezen.
              </span>
            </div>
          )}
        </div>
      </Modal>
    </Card>
  );
}